import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { InstallPrompt } from "@/components/InstallPrompt";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Schemes from "@/pages/Schemes";
import Documents from "@/pages/Documents";
import DocumentDetail from "@/pages/DocumentDetail";
import DocumentUpload from "@/pages/DocumentUpload";
import Coverage from "@/pages/Coverage";
import Deadlines from "@/pages/Deadlines";
import Chat from "@/pages/Chat";
import Mappa from "@/pages/Mappa";
import Redundancies from "@/pages/Redundancies";
import BulkImport from "@/pages/BulkImport";
import Audits from "@/pages/Audits";
import Nomenclatura from "@/pages/Nomenclatura";
import FontiNormative from "@/pages/FontiNormative";
import Employees from "@/pages/Employees";
import Suppliers from "@/pages/Suppliers";
import Reviews from "@/pages/Reviews";
import Incidents from "@/pages/Incidents";
import TrainingSessions from "@/pages/TrainingSessions";
import AiUsage from "@/pages/AiUsage";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-blue-700" />
    </div>
  );
}

function Protected({ children, consultantOnly, superAdminOnly }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (consultantOnly && user.role !== "consultant")
    return <Navigate to="/documenti" replace />;
  if (superAdminOnly && !user.super_admin)
    return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invito/:token" element={<AcceptInvite />} />
      <Route
        element={
          <Protected>
            <TenantProvider>
              <AppLayout />
            </TenantProvider>
          </Protected>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/documenti" element={<Documents />} />
        <Route path="/documenti/nuovo" element={<Protected consultantOnly><DocumentUpload /></Protected>} />
        <Route path="/documenti/importa" element={<Protected consultantOnly><BulkImport /></Protected>} />
        <Route path="/documenti/:id" element={<DocumentDetail />} />
        <Route path="/assistente" element={<Chat />} />
        <Route path="/mappa" element={<Mappa />} />
        <Route path="/ridondanze" element={<Protected consultantOnly><Redundancies /></Protected>} />
        <Route path="/clienti" element={<Protected consultantOnly><Clients /></Protected>} />
        <Route path="/schemi" element={<Protected consultantOnly><Schemes /></Protected>} />
        <Route path="/copertura" element={<Protected><Coverage /></Protected>} />
        <Route path="/scadenze" element={<Protected><Deadlines /></Protected>} />
      <Route path="/audit" element={<Protected><Audits /></Protected>} />
      <Route path="/nomenclatura" element={<Protected consultantOnly><Nomenclatura /></Protected>} />
      <Route path="/fonti-normative" element={<Protected consultantOnly><FontiNormative /></Protected>} />
      <Route path="/dipendenti" element={<Protected><Employees /></Protected>} />
      <Route path="/fornitori" element={<Protected><Suppliers /></Protected>} />
      <Route path="/riesame" element={<Protected consultantOnly><Reviews /></Protected>} />
      <Route path="/incidenti" element={<Protected><Incidents /></Protected>} />
      <Route path="/schede-formative" element={<Protected><TrainingSessions /></Protected>} />
      <Route path="/consumi-ai" element={<Protected superAdminOnly><AiUsage /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <InstallPrompt />
      </AuthProvider>
    </div>
  );
}

export default App;
